// Screen 6, the ACO decile view (SPEC §8 Screen 6; CR-002 CR2-040; J-065). The ACO is the path's; the
// year comes from the shell's selector and defaults to the newest with results for this ACO (J-209).
// Choosing another ACO in the shell opens that ACO's page; "All" keeps this one. The configured texts are
// read here, where features are composed, so the feature does not import the shell's API (C6).
// J-209 (the owner's inbox file 06, PR D): a failed or refused read says so (item 7), and a practice
// role reads why the screen is not theirs (item 16).
import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { uiDocumentSchema } from "@vbltc/schema";

import { metadataQueryOptions } from "@/features/aco-shell/api/metadata";
import { uiConfigQueryOptions } from "@/features/aco-shell/api/ui-config";
import { AcoNotFound } from "@/features/aco-shell/components/aco-not-found";
import { ShellStatusMessage } from "@/features/aco-shell/components/shell-status-message";
import {
  ALL_ACOS,
  defaultAcoCode,
  landingPath,
  resolveYear,
} from "@/features/aco-shell/default-aco";
import { shellStatus } from "@/features/aco-shell/shell-status";
import { AcoSummaryScreen } from "@/features/aco-summary/components/aco-summary-screen";
import { usePermission } from "@/features/auth/use-permission";

export const Route = createFileRoute("/_authenticated/aco/$acoCode")({
  // The shell draws the permission state for a role or account the summary's read refuses (J-209).
  staticData: { screenName: "ACO Summary", requires: { acoQuality: ["read"] } },
  component: AcoSummaryPage,
});

function AcoSummaryPage() {
  const { acoCode } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const canLoadData = usePermission({ acoData: ["load"] });
  const metadata = useQuery(metadataQueryOptions());
  const ui = useQuery(uiConfigQueryOptions());
  const chosen = search.aco;
  React.useEffect(() => {
    if (chosen === undefined || chosen === acoCode) return;
    // J-215 (inbox 22 item 3): "All" keeps this page, and the URL then names this page's ACO, as the
    // header does; `?aco=ALL` beside `/aco/<code>` said two things at once.
    if (chosen === ALL_ACOS)
      void navigate({
        to: "/aco/$acoCode",
        params: { acoCode },
        search: { ...search, aco: acoCode },
        replace: true,
      });
    // J-299 (QB4 item 11a): replace, so Back does not return to the `?aco=` entry and come here again.
    else void navigate({ to: "/aco/$acoCode", params: { acoCode: chosen }, search, replace: true });
  }, [chosen, acoCode, navigate, search]);
  const document = uiDocumentSchema.safeParse(ui.data?.document);
  const status = shellStatus(metadata, { data: ui.data, error: ui.error, valid: document.success });
  if (status !== "ready" || metadata.data === undefined || !document.success)
    return (
      <ShellStatusMessage
        status={status === "ready" ? "loading" : status}
        onRetry={() => void Promise.all([metadata.refetch(), ui.refetch()])}
      />
    );
  // J-200: an ACO not in the caller's scope (retired, unknown, or not granted) is "not found", with a
  // link to the default ACO; metadata lists no retired ACO.
  if (!metadata.data.acos.some((a) => a.acoCode === acoCode)) {
    const data = metadata.data;
    return (
      <AcoNotFound
        acoCode={acoCode}
        fallback={defaultAcoCode(data.acos)}
        renderLink={(fallback) =>
          fallback === undefined ? (
            <Link to={landingPath(data, canLoadData)} className="underline">
              Go to the start page
            </Link>
          ) : (
            <Link to="/aco/$acoCode" params={{ acoCode: fallback }} className="underline">
              Open {fallback}
            </Link>
          )
        }
      />
    );
  }
  const year = resolveYear(metadata.data, search.year, acoCode);
  if (year === undefined)
    return <p className="text-muted-foreground text-sm">No performance year is configured.</p>;
  const { text } = document.data;
  return (
    <AcoSummaryScreen
      acoCode={acoCode}
      year={year}
      text={{
        inverseLabel: text.inverse_label,
        completenessTooltip: text.completeness_tooltip,
        scoredRateLabel: text.scored_rate_label,
      }}
    />
  );
}
